import { useState } from 'react';
import Papa from 'papaparse';
import { Upload, AlertCircle } from 'lucide-react';

interface UploadFormProps {
  locationId: string;
  onUploadComplete: (batchId: string) => void;
  onCancel?: () => void;
}

export function UploadForm({ locationId, onUploadComplete, onCancel }: UploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [batchName, setBatchName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleData, setSampleData] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [step, setStep] = useState<'upload' | 'map' | 'processing'>('upload');
  const [error, setError] = useState<string | null>(null);
  const [servicesEnabled, setServicesEnabled] = useState({
    landline: true,
    tcpa: true,
  });
  const [isDragging, setIsDragging] = useState(false);

  const validateAndSetFile = (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    validateAndSetFile(selectedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleContinueToMapping = () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }
    if (!batchName.trim()) {
      setError('Please enter a batch name');
      return;
    }

    setError(null);

    Papa.parse(file, {
      preview: 2,
      complete: (results) => {
        const firstRow = results.data?.[0] as string[] | undefined;
        const hasUsableHeader = Array.isArray(firstRow)
          && firstRow.some((h) => typeof h === 'string' && h.trim().length > 0);

        if (hasUsableHeader) {
          setHeaders(firstRow as string[]);
          // Capture first data row as sample
          if (results.data[1]) {
            setSampleData(results.data[1] as string[]);
          }
          setStep('map');
        } else {
          // No readable header row — empty file, a blank first line, or a
          // non-CSV file (e.g. an .xlsx renamed to .csv, or a UTF-16 export).
          // Give the user a clear next action instead of a silently dead button.
          setError(
            "We couldn't read any columns from this file. It may be empty, start with a " +
            "blank line, or not be a standard CSV (for example an Excel file). Please re-save " +
            "it as “CSV UTF-8” or try a different file."
          );
        }
      },
      error: (err) => {
        setError(
          `We couldn't read this file (${err.message}). Please re-save it as “CSV UTF-8” or try a different file.`
        );
      },
    });
  };

  const handleCancel = () => {
    resetForm();
    if (onCancel) {
      onCancel();
    }
  };

  const handleMapping = (csvColumn: string, field: string) => {
    setMapping((prev) => {
      const newMapping = { ...prev };
      
      if (field === '') {
        delete newMapping[csvColumn];
        return newMapping;
      }
      
      Object.keys(newMapping).forEach((key) => {
        if (key !== csvColumn && newMapping[key] === field) {
          delete newMapping[key];
        }
      });
      
      newMapping[csvColumn] = field;
      return newMapping;
    });
  };

  const handleSubmit = async () => {
    if (!file || !batchName) {
      setError('Please provide a batch name and select a file');
      return;
    }

    const phoneColumn = Object.keys(mapping).find((key) => mapping[key] === 'phone');
    const firstNameColumn = Object.keys(mapping).find((key) => mapping[key] === 'first_name');

    if (!phoneColumn || !firstNameColumn) {
      setError('Please map all required fields: Phone Number and First Name');
      return;
    }

    setStep('processing');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchName', batchName);
      formData.append('locationId', locationId);
      formData.append('mapping', JSON.stringify(mapping));
      formData.append('servicesEnabled', JSON.stringify(servicesEnabled));

      const response = await fetch(`${import.meta.env.VITE_API_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json() as {
        success: boolean;
        batchId?: string;
        error?: string;
      };

      if (data.success && data.batchId) {
        onUploadComplete(data.batchId);
        resetForm();
      } else {
        const errorMsg = data.error || 'Upload failed';
        console.error('[Upload] Upload failed:', errorMsg);
        setError(errorMsg);
        setStep('map');
      }
    } catch (err) {
      console.error('[Upload] Exception during upload:', err);
      setError(`Upload failed: ${err instanceof Error ? err.message : 'Please try again.'}`);
      setStep('map');
    }
  };

  const resetForm = () => {
    setFile(null);
    setBatchName('');
    setHeaders([]);
    setMapping({});
    setStep('upload');
    setError(null);
  };

  if (step === 'upload') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Upload CSV File</h2>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., January Leads"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CSV File <span className="text-red-600">*</span>
            </label>
            <div
              className="flex items-center justify-center w-full"
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}>
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className={`w-10 h-10 mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                  <p className="text-sm text-gray-600">
                    {file ? file.name : 'Drag & drop or click to upload CSV file'}
                  </p>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={servicesEnabled.landline}
                onChange={(e) => setServicesEnabled({ ...servicesEnabled, landline: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Check for Landlines</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={servicesEnabled.tcpa}
                onChange={(e) => setServicesEnabled({ ...servicesEnabled, tcpa: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Check TCPA Litigator List</span>
            </label>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleContinueToMapping}
              disabled={!file || !batchName.trim()}
              className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'map') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Map CSV Columns</h2>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-6">
          <div className="grid grid-cols-2 gap-4 mb-3 pb-2 border-b-2 border-gray-300">
            <div className="font-semibold text-gray-700">Found In Your File</div>
            <div className="font-semibold text-gray-700">System Field To Map To</div>
          </div>
          <div className="space-y-3">
            {headers.map((header, index) => (
              <div key={index} className="grid grid-cols-2 gap-4 items-start">
                <div>
                  <div className="font-medium text-gray-700">{header}</div>
                  {sampleData[index] && (
                    <div className="text-sm text-gray-500 italic mt-1">[Ex. {sampleData[index]}]</div>
                  )}
                </div>
                <select
                value={mapping[`col_${index}`] || ''}
                onChange={(e) => handleMapping(`col_${index}`, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Skip --</option>
                <option value="first_name" disabled={Object.values(mapping).includes('first_name') && mapping[`col_${index}`] !== 'first_name'}>First Name</option>
                <option value="last_name" disabled={Object.values(mapping).includes('last_name') && mapping[`col_${index}`] !== 'last_name'}>Last Name</option>
                <option value="phone" disabled={Object.values(mapping).includes('phone') && mapping[`col_${index}`] !== 'phone'}>Phone Number</option>
                <option value="email" disabled={Object.values(mapping).includes('email') && mapping[`col_${index}`] !== 'email'}>Email</option>
                <option value="phy_address" disabled={Object.values(mapping).includes('phy_address') && mapping[`col_${index}`] !== 'phy_address'}>Physical Address</option>
                <option value="state" disabled={Object.values(mapping).includes('state') && mapping[`col_${index}`] !== 'state'}>State</option>
                <option value="zip_code" disabled={Object.values(mapping).includes('zip_code') && mapping[`col_${index}`] !== 'zip_code'}>ZIP Code</option>
                <option value="company" disabled={Object.values(mapping).includes('company') && mapping[`col_${index}`] !== 'company'}>Company</option>
                <option value="notes" disabled={Object.values(mapping).includes('notes') && mapping[`col_${index}`] !== 'notes'}>Notes</option>
                <option value="csv_carrier" disabled={Object.values(mapping).includes('csv_carrier') && mapping[`col_${index}`] !== 'csv_carrier'}>Carrier</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setStep('upload')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={!Object.values(mapping).includes('phone') || !Object.values(mapping).includes('first_name')}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Processing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Uploading and processing...</p>
      </div>
    </div>
  );
}
